<template>
  <!--
    「요금·결제」의 구독 상세 창(설계안 2판 §10.1 · 3판 §20.5 · 선결제 §21.7 · 운영자 화면 §25) — 구독 칸 · 지정 칸(요금제·지정 끝·때·누가·
    사유) · 결제 방식 칸(결제 방식·결제 카드(있을 때)·지금 낸 기간·다음 결제) · 해지 칸(누가·사유) · 작업 기록(있을 때). 단추는 그 단추가 바꾸는 칸의 머리 줄
    오른쪽에 둔다(§24): 구독 [체험 기간 정하기] · 지정 [요금제 지정/지정 바꾸기][지정 풀기] · 결제 방식 [지금 청구][이용 기간 늘리기][청구
    내역] · 해지 [신청 취소][자동 결제 멈추기][중도 해지·환불], 바닥은 [닫기] 하나.
    **어느 단추가 보이는지는 서버가 정한다**(§25.2) — 상세의 `actions` 에 그 동작이 있고 세션이 그 입구의 액션을 가질 때만(`opAllowed`).
    이 창은 상태·신청 칸으로 따로 판정하지 않는다([청구 내역]은 읽기라 권한만). 요청은 이 창이 가진 `subscriptionId` 로(상세를 다시 읽는
    동안에도). 금액은 서버가 계산한 값을 옮길 뿐이다. 말·요청은 `utils/subscriptionAdmin` 한 곳.
  -->
  <el-dialog
    :title="$t(text.detailTitle)"
    :visible="visible"
    width="560px"
    top="5vh"
    data-testid="subscription-detail"
    @update:visible="$emit('update:visible', $event)"
    @open="load">
    <div v-if="detail" class="subscription-detail">
      <div class="subscription-detail__head">
        <SubscriptionBadge :tone="statusView.tone">{{ $t(statusView.label) }}</SubscriptionBadge>
        <SubscriptionBadge v-for="mark in statusView.marks" :key="mark.label" :tone="mark.tone" outline>{{
          $t(mark.label)
        }}</SubscriptionBadge>
        <span class="subscription-detail__id">#{{ detail.subscriptionId }}</span>
      </div>

      <div class="subscription-detail__section" data-testid="subscription-head-basic">
        <h4>{{ $t(text.sectionBasic) }}</h4>
        <el-button
          v-if="allows(OP.TRIAL_SET)"
          size="mini"
          plain
          data-testid="subscription-trial-end"
          @click="trialEndVisible = true"
          >{{ $t(text.trialSet) }}</el-button
        >
      </div>
      <dl class="subscription-detail__rows" data-testid="subscription-detail-basic">
        <template v-for="row in rows.basic">
          <dt :key="`${row.label}-dt`">{{ $t(row.label) }}</dt>
          <dd :key="`${row.label}-dd`">{{ row.value }}</dd>
        </template>
      </dl>

      <div class="subscription-detail__section" data-testid="subscription-head-grant">
        <h4>{{ $t(text.sectionGrant) }}</h4>
        <el-button
          v-if="allows(OP.GRANT)"
          size="mini"
          type="primary"
          plain
          data-testid="subscription-grant"
          @click="grantVisible = true"
          >{{ $t(grantActionText(detail.grant)) }}</el-button
        >
        <el-button
          v-if="allows(OP.UNGRANT)"
          size="mini"
          plain
          data-testid="subscription-ungrant"
          @click="openReason('ungrant')"
          >{{ $t(text.ungrant) }}</el-button
        >
      </div>
      <dl v-if="grantRows.length" class="subscription-detail__rows" data-testid="subscription-detail-grant">
        <template v-for="row in grantRows">
          <dt :key="`${row.label}-dt`">{{ $t(row.label) }}</dt>
          <dd :key="`${row.label}-dd`">{{ row.value }}</dd>
        </template>
      </dl>
      <p v-if="!detail.grant" class="subscription-detail__none" data-testid="subscription-detail-not-granted">
        {{ $t(text.notGranted) }}
      </p>

      <div class="subscription-detail__section" data-testid="subscription-head-payment">
        <h4>{{ $t(text.sectionTerm) }}</h4>
        <el-button
          v-if="allows(OP.CHARGE_NOW)"
          size="mini"
          type="primary"
          plain
          data-testid="subscription-charge-now"
          @click="chargeNowVisible = true"
          >{{ $t(text.chargeNow) }}</el-button
        >
        <el-button
          v-if="allows(OP.EXTEND)"
          size="mini"
          plain
          data-testid="subscription-extend"
          @click="extendVisible = true"
          >{{ $t(text.extend) }}</el-button
        >
        <el-button
          v-if="canCharges"
          size="mini"
          plain
          data-testid="subscription-show-charges"
          @click="$emit('show-charges', detail.subscriptionId)"
          >{{ $t(text.showCharges) }}</el-button
        >
      </div>
      <dl class="subscription-detail__rows" data-testid="subscription-detail-payment">
        <template v-for="row in rows.payment">
          <dt :key="`${row.label}-dt`">{{ $t(row.label) }}</dt>
          <dd :key="`${row.label}-dd`">{{ row.value }}</dd>
        </template>
      </dl>

      <div class="subscription-detail__section" data-testid="subscription-head-cancel">
        <h4>{{ $t(text.sectionCancel) }}</h4>
        <el-button
          v-if="allows(OP.WITHDRAW)"
          size="mini"
          plain
          data-testid="subscription-withdraw"
          @click="openReason('withdraw')"
          >{{ $t(text.withdraw) }}</el-button
        >
        <el-button
          v-if="allows(OP.STOP)"
          size="mini"
          type="danger"
          plain
          data-testid="subscription-stop"
          @click="openReason('stop')"
          >{{ $t(text.stop) }}</el-button
        >
        <el-button
          v-if="allows(OP.TERMINATE)"
          size="mini"
          type="danger"
          plain
          data-testid="subscription-terminate"
          @click="terminateVisible = true"
          >{{ $t(text.terminate) }}</el-button
        >
      </div>
      <dl v-if="rows.cancel.length" class="subscription-detail__rows" data-testid="subscription-detail-cancel">
        <template v-for="row in rows.cancel">
          <dt :key="`${row.label}-dt`">{{ $t(row.label) }}</dt>
          <dd :key="`${row.label}-dd`">{{ row.value }}</dd>
        </template>
      </dl>
      <p v-else class="subscription-detail__none">{{ $t(text.notCanceled) }}</p>

      <template v-if="logLines.length">
        <div class="subscription-detail__section" data-testid="subscription-head-logs">
          <h4>{{ $t(text.sectionLogs) }}</h4>
        </div>
        <ul class="subscription-detail__logs" data-testid="subscription-detail-logs">
          <li v-for="line in logLines" :key="line.logId">{{ line.text }}</li>
        </ul>
      </template>
    </div>
    <p v-else-if="loadFailed" class="subscription-detail__none">{{ $t(text.loadFailed) }}</p>

    <span slot="footer">
      <el-button size="small" data-testid="subscription-detail-close" @click="$emit('update:visible', false)">{{
        $t(text.close)
      }}</el-button>
    </span>

    <SubscriptionReasonDialog
      :visible.sync="reasons.stop.visible"
      testid="subscription-stop"
      :title="text.stopTitle"
      :store-label="storeLabel"
      :intro="stopNoticeOf(detail)"
      :reason-label="text.stopReason"
      :submit-label="text.stop"
      submit-type="danger"
      :problem="reasons.stop.problem"
      :busy="reasons.stop.busy"
      @submit="submitStop" />

    <SubscriptionReasonDialog
      :visible.sync="reasons.ungrant.visible"
      testid="subscription-ungrant"
      :title="text.ungrantTitle"
      :store-label="storeLabel"
      :intro="text.ungrantIntro"
      :notice="ungrantNoticeOf(detail)"
      :reason-label="text.ungrantReason"
      :submit-label="text.ungrant"
      :problem="reasons.ungrant.problem"
      :busy="reasons.ungrant.busy"
      @submit="submitUngrant" />

    <SubscriptionReasonDialog
      :visible.sync="reasons.withdraw.visible"
      testid="subscription-withdraw"
      :title="text.withdraw"
      :store-label="storeLabel"
      :intro="text.withdrawIntro"
      :reason-label="text.cancelReason"
      :submit-label="text.withdraw"
      :problem="reasons.withdraw.problem"
      :busy="reasons.withdraw.busy"
      @submit="submitWithdraw" />

    <SubscriptionGrantDialog
      :visible.sync="grantVisible"
      :store-cd="detail ? detail.storeCd : null"
      :store-label="storeLabel"
      :grant="detail ? detail.grant : null"
      @granted="onWritten" />

    <SubscriptionTerminateDialog
      :visible.sync="terminateVisible"
      :subscription-id="subscriptionId"
      :store-label="storeLabel"
      @changed="onWritten" />

    <SubscriptionTrialEndDialog
      :visible.sync="trialEndVisible"
      :subscription-id="subscriptionId"
      :detail="detail"
      :store-label="storeLabel"
      @changed="onWritten" />

    <SubscriptionChargeNowDialog
      :visible.sync="chargeNowVisible"
      :subscription-id="subscriptionId"
      :detail="detail"
      :store-label="storeLabel"
      @changed="onWritten" />

    <SubscriptionExtendDialog
      :visible.sync="extendVisible"
      :subscription-id="subscriptionId"
      :extend="detail ? detail.extend : null"
      :store-label="storeLabel"
      @changed="onWritten" />
  </el-dialog>
</template>

<script>
import SubscriptionBadge from '@/components/subscription/SubscriptionBadge.vue'
import SubscriptionChargeNowDialog from '@/components/subscription/SubscriptionChargeNowDialog.vue'
import SubscriptionExtendDialog from '@/components/subscription/SubscriptionExtendDialog.vue'
import SubscriptionGrantDialog from '@/components/subscription/SubscriptionGrantDialog.vue'
import SubscriptionReasonDialog from '@/components/subscription/SubscriptionReasonDialog.vue'
import SubscriptionTerminateDialog from '@/components/subscription/SubscriptionTerminateDialog.vue'
import SubscriptionTrialEndDialog from '@/components/subscription/SubscriptionTrialEndDialog.vue'
import mainVueMixin from '@/mixins/mainVueMixin'
import { httpErrorMessage } from '@/plugins/http/policy'
import {
  SUBSCRIPTION_ADMIN_ACTION,
  SUBSCRIPTION_ADMIN_API,
  SUBSCRIPTION_OP,
  SUBSCRIPTION_TEXT,
  SUBSCRIPTION_WRITE_CONFIG,
  detailRowsOf,
  grantActionText,
  grantRowsOf,
  isNotGranted,
  logLinesOf,
  opAllowed,
  stopNoticeOf,
  stopRequestOf,
  subscriberText,
  subscriptionStatusViewOf,
  ungrantNoticeOf,
  ungrantRequestOf,
  withdrawRequestOf,
} from '@/utils/subscriptionAdmin'

/** 사유 창 하나의 상태 — 열림 · 고칠 말 · 보내는 중(적은 사유는 창이 갖는다) */
const closedReason = () => ({ visible: false, problem: '', busy: false })

export default {
  name: 'SubscriptionDetailDialog',

  mixins: [mainVueMixin],

  components: {
    SubscriptionBadge,
    SubscriptionChargeNowDialog,
    SubscriptionExtendDialog,
    SubscriptionGrantDialog,
    SubscriptionReasonDialog,
    SubscriptionTerminateDialog,
    SubscriptionTrialEndDialog,
  },

  props: {
    visible: { type: Boolean, default: false },
    subscriptionId: { type: Number, default: null },
  },

  data: function () {
    return {
      text: SUBSCRIPTION_TEXT,
      OP: SUBSCRIPTION_OP,
      /** `GET /api/admin/subscriptions/{id}` 응답 — 읽기 전·실패면 `null` */
      detail: null,
      loadFailed: false,
      /** 사유만 받는 창 셋(`SubscriptionReasonDialog`) */
      reasons: { stop: closedReason(), ungrant: closedReason(), withdraw: closedReason() },
      grantVisible: false,
      terminateVisible: false,
      trialEndVisible: false,
      chargeNowVisible: false,
      extendVisible: false,
    }
  },

  computed: {
    statusView: function () {
      return subscriptionStatusViewOf(this.detail)
    },
    rows: function () {
      return detailRowsOf(this.detail)
    },
    grantRows: function () {
      return grantRowsOf(this.detail)
    },
    logLines: function () {
      return logLinesOf(this.detail)
    },
    /** 동작 창 머리의 「누구의 구독」 — 매장(POS2 Lite) 또는 사장님(오더엔 멀티) */
    storeLabel: function () {
      return subscriberText(this.detail)
    },
    canCharges: function () {
      return this.checkAuthActionAccess(SUBSCRIPTION_ADMIN_ACTION.CHARGE_SEARCH)
    },
  },

  methods: {
    grantActionText,
    stopNoticeOf,
    ungrantNoticeOf,

    /** 이 동작의 단추를 그리는가 — 서버의 `actions` ∧ 세션 권한(`utils/subscriptionAdmin.opAllowed` 한 규칙) */
    allows: function (op) {
      return opAllowed(this.detail, op, this.checkAuthActionAccess)
    },

    /** 창이 열릴 때마다 새로 읽는다 — 목록 행은 그 사이 바뀌었을 수 있다(회차 청구·앱 해지) */
    load: async function () {
      this.detail = null
      this.loadFailed = false
      if (this.subscriptionId === null) return

      const outcome = await this.mainVue.$axios.search(
        `${SUBSCRIPTION_ADMIN_API}/${this.subscriptionId}`,
        null,
        function (result) {
          this.detail = result
        }.bind(this)
      )

      if (!outcome.ok) this.loadFailed = true
    },

    openReason: function (key) {
      this.reasons[key] = Object.assign(closedReason(), { visible: true })
    },

    /** 쓰기가 끝났다 — 목록과 이 창을 다시 읽는다(응답의 상세를 그대로 앉히지 않는다 — 읽는 길은 [load] 하나) */
    onWritten: function () {
      this.$emit('changed')
      this.load()
    },

    /**
     * 사유만 받는 쓰기를 보낸다(멈추기·풀기·신청 취소) — 보내고, 말하고, 상세·목록을 다시 읽는다. 거절은 서버 문장을 그 창 안에서 한 번
     * 말한다 — 409(그 사이 바뀜 · 결과 모르는 결제를 확인하는 중 `CHARGE_PENDING`)면 창을 열어 둔 채 다시 읽는다(청구 취소와 같은 규칙).
     * `settled` 는 「실패가 아니라 답」인 거절(풀기의 409 `NOT_GRANTED` — 그 사이 이미 풀림): 창을 닫고 그 말을 하고 다시 읽는다.
     *
     * @param {string} key `reasons` 의 칸
     * @param {object} request 요청 함수의 값(`{ok, url, body}` 또는 `{ok: false, problem}`)
     * @param {string} doneText 끝난 말
     * @param {?{matches: function(*): boolean, text: string}} settled
     */
    sendReason: async function (key, request, doneText, settled) {
      const state = this.reasons[key]
      if (!request.ok) {
        state.problem = this.$t(request.problem)
        return
      }

      state.busy = true
      state.problem = ''
      try {
        await this.$axios.post(request.url, request.body, SUBSCRIPTION_WRITE_CONFIG)

        state.visible = false
        this.mainVue.$message({ type: 'success', message: this.$t(doneText) })
        this.onWritten()
      } catch (error) {
        if (settled && settled.matches(error)) {
          state.visible = false
          this.mainVue.$message({ type: 'warning', message: this.$t(settled.text) })
          this.onWritten()
          return
        }

        state.problem = httpErrorMessage(error, this.$t(this.text.writeFailed))
        if (error && error.status === 409) this.onWritten()
      } finally {
        state.busy = false
      }
    },

    /** 자동 결제 멈추기(`POST …/stop` — 해지, §21.4) */
    submitStop: function (reason) {
      return this.sendReason('stop', stopRequestOf(this.subscriptionId, { reason }), this.text.stopDone)
    },

    /** 신청 취소(`POST …/withdraw` — §25.6, 체험·유예는 그대로) */
    submitWithdraw: function (reason) {
      return this.sendReason('withdraw', withdrawRequestOf(this.subscriptionId, { reason }), this.text.withdrawDone)
    },

    /** 지정 풀기(`POST …/ungrant` — §20.5) — 409 `NOT_GRANTED` 는 「그 사이 풀림」으로 받는다 */
    submitUngrant: function (reason) {
      return this.sendReason('ungrant', ungrantRequestOf(this.subscriptionId, { reason }), this.text.ungrantDone, {
        matches: isNotGranted,
        text: this.text.notGrantedChanged,
      })
    },
  },
}
</script>

<style scoped>
/* 칸 머리 — 제목 왼쪽, 그 칸을 바꾸는 단추 오른쪽, 한 줄 가운데 맞춤. 단추가 없는 머리도 같은 높이(작은 단추 28px) */
.subscription-detail__section {
  display: flex;
  align-items: center;
  min-height: 28px;
  margin: 14px 0 6px;
}

.subscription-detail h4 {
  margin: 0 auto 0 0;
  font-size: 14px;
  font-weight: 600;
  color: var(--color-text-primary);
}

.subscription-detail__head {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
}

.subscription-detail__id {
  margin-left: auto;
  color: var(--color-text-secondary);
}

.subscription-detail__rows {
  display: grid;
  grid-template-columns: 140px 1fr;
  gap: 4px 16px;
  margin: 0;
}

.subscription-detail__rows dt {
  color: var(--color-text-secondary);
}

.subscription-detail__rows dd {
  margin: 0;
  color: var(--color-text-primary);
}

.subscription-detail__none {
  margin: 0;
  color: var(--color-text-secondary);
}

/* 작업 기록 — 최근 50 줄까지 오므로 창이 길어지지 않게 칸 안에서 넘긴다 */
.subscription-detail__logs {
  max-height: 180px;
  margin: 0;
  padding: 0;
  overflow-y: auto;
  list-style: none;
  font-size: 12px;
  color: var(--color-text-regular);
  word-break: keep-all;
}

.subscription-detail__logs li + li {
  margin-top: 4px;
}
</style>
